import React from 'react';

import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { getColorStylesByRecord } from 'PDF/helpers/documentStyle';
import { ITimelineItem, StageStatusEnum, TimelineType } from 'PDF/types/timeline';

import { formatDateToMonthYear } from './utils';
import { colorIndexesDevelopTimeLine } from '../DevelopmentTimeline/colors';
import { getDisplayConditionFunc } from '../DevelopmentTimeline/utils';

const A4_PDF_WIDTH = 595;
const PDF_PAGE_LEFT_RIGHT_PADDINGS = 36;
const DATE_WIDTH = 33;

interface IDevelopmentTimelineRowProps {
  dates: string[];
  item: ITimelineItem;
  width: number;
  type: TimelineType;
}

export const ProgressRow: React.FC<IDevelopmentTimelineRowProps> = ({ dates, item, width, type }) => {
  const checkCondition = getDisplayConditionFunc(type);
  const [doneColor, inProgressColor, toDoColor] = getColorStylesByRecord(colorIndexesDevelopTimeLine);
  const ceilContainerWidth = (A4_PDF_WIDTH - PDF_PAGE_LEFT_RIGHT_PADDINGS * 2 - width) / dates.length;

  const formattedStartDate = formatDateToMonthYear(item.startDate);
  const formattedEndDate = formatDateToMonthYear(item.endDate);

  const ceilPaddings = (ceilContainerWidth - DATE_WIDTH) / 2;

  return (
    <View style={styles.rowWrap}>
      <Text
        style={[
          styles.heading,
          {
            minWidth: width,
            maxWidth: width,
          },
        ]}
      >
        {item.stage}
      </Text>
      {dates.map((date) => (
        <View
          key={date}
          style={[
            styles.ceil,
            {
              width: `${ceilContainerWidth}px`,
              ...(formattedStartDate === date && {
                paddingLeft: `${ceilPaddings}px`,
              }),
              ...(formattedEndDate === date && {
                paddingRight: `${ceilPaddings}px`,
              }),
            },
          ]}
        >
          {checkCondition(new Date(item.startDate), new Date(item.endDate), date) && (
            <View
              style={[
                styles.indicator,
                {
                  backgroundColor:
                    item.status === StageStatusEnum.DONE
                      ? doneColor
                      : item.status === StageStatusEnum.IN_PROGRESS || !item.status
                        ? inProgressColor
                        : toDoColor,
                },
              ]}
            ></View>
          )}
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  ceil: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  data: {
    fontFamily: 'Noto',
    fontSize: '9px',
    fontWeight: 600,
    color: '#000000',
  },
  rowWrap: {
    width: '100%',
    minHeight: '24px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: '#212121',
    borderBottom: '0.3px dotted #77787C',
    paddingTop: '7px',
    paddingBottom: '7px',
  },
  heading: {
    fontFamily: 'Noto',
    fontSize: '9px',
    fontWeight: 300,
    color: '#000000',
  },
  indicator: {
    width: '100.2%',
    height: '4px',
  },
});
